import { addUnits, type CalendarUnit, type Day } from '../core/calendar.ts';
import { dec, div, fromDouble, mul, type Decimal } from '../core/decimal.ts';
import type { Frequency } from '../types/domain.ts';

/** One recurrence step: `count` calendar units. */
export interface Step {
  readonly unit: CalendarUnit;
  readonly count: number;
}

/**
 * The calendar step of a recurring frequency (weekly = 1 week, fortnightly = 2 weeks,
 * quarterly = 3 months, custom = its interval, possibly ≤ 0); null for oneTime.
 */
export function frequencyStep(frequency: Frequency): Step | null {
  switch (frequency) {
    case 'oneTime':
      return null;
    case 'daily':
      return { unit: 'day', count: 1 };
    case 'weekly':
      return { unit: 'week', count: 1 };
    case 'fortnightly':
      return { unit: 'week', count: 2 };
    case 'monthly':
      return { unit: 'month', count: 1 };
    case 'quarterly':
      return { unit: 'month', count: 3 };
    case 'yearly':
      return { unit: 'year', count: 1 };
    default:
      return { unit: frequency.unit, count: frequency.interval };
  }
}

/** TransactionFrequency.annualOccurrences (a Double in Swift). */
export function annualOccurrences(frequency: Frequency): number {
  switch (frequency) {
    case 'oneTime':
    case 'yearly':
      return 1;
    case 'daily':
      return 365;
    case 'weekly':
      return 52;
    case 'fortnightly':
      return 26;
    case 'monthly':
      return 12;
    case 'quarterly':
      return 4;
    default: {
      const perYear = { day: 365, week: 52, month: 12, year: 1 }[frequency.unit];
      return perYear / frequency.interval;
    }
  }
}

/** TransactionFrequency.monthlyEquivalent(of:): amount × Decimal(annualOccurrences) / 12. */
export const monthlyEquivalent = (frequency: Frequency, amount: Decimal): Decimal =>
  div(mul(amount, fromDouble(annualOccurrences(frequency))), dec(12));

/**
 * Schedule.date: the n-th (0-based) date of a schedule anchored at `start`, in one calendar
 * addition, so month ends clamp without drifting (31 Jan → 29 Feb → 31 Mar). Every recurring
 * schedule steps this way (R6).
 */
export const scheduleDate = (start: Day, step: Step, n: number): Day => addUnits(start, step.unit, step.count * n);
